import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  BrainCircuit, Target, MessageSquare, TrendingUp, TrendingDown,
  ChevronRight, AlertTriangle, CheckCircle2, Info, Sparkles,
  Clock, Users, Phone, ArrowRight, Shield, Zap,
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SKILL_META = {
  booking_timing: { label: 'Booking Timing', icon: Clock, color: 'emerald' },
  qualification_quality: { label: 'Qualification', icon: Users, color: 'sky' },
  objection_handling: { label: 'Objection Handling', icon: Shield, color: 'amber' },
  conversation_flow: { label: 'Conversation Flow', icon: MessageSquare, color: 'violet' },
  escalation_accuracy: { label: 'Escalation', icon: Phone, color: 'rose' },
};

const INSIGHT_ICON = {
  success: { icon: CheckCircle2, color: 'text-emerald-400', bg: 'bg-emerald-500/8', border: 'border-emerald-500/15' },
  warning: { icon: AlertTriangle, color: 'text-amber-400', bg: 'bg-amber-500/8', border: 'border-amber-500/15' },
  info: { icon: Info, color: 'text-sky-400', bg: 'bg-sky-500/8', border: 'border-sky-500/15' },
};

function SkillBar({ name, score }) {
  const meta = SKILL_META[name];
  if (!meta) return null;
  const Icon = meta.icon;
  const barColor = score >= 70 ? 'bg-emerald-500' : score >= 50 ? 'bg-amber-500' : 'bg-red-500';

  return (
    <div className="space-y-1" data-testid={`skill-${name}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Icon size={11} className="text-muted-foreground" />
          <span className="text-[10px] text-muted-foreground">{meta.label}</span>
        </div>
        <span className={`text-[10px] font-bold ${score >= 70 ? 'text-emerald-400' : score >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
          {score}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
        <div className={`h-full rounded-full ${barColor} transition-all duration-700`} style={{ width: `${score}%` }} />
      </div>
    </div>
  );
}

function InsightRow({ insight, onAction }) {
  const st = INSIGHT_ICON[insight.type] || INSIGHT_ICON.info;
  const IconComp = st.icon;
  return (
    <div className={`flex gap-2.5 p-2.5 rounded-lg border ${st.bg} ${st.border}`} data-testid={`insight-${insight.type}`}>
      <IconComp size={13} className={`${st.color} shrink-0 mt-0.5`} />
      <div className="flex-1 min-w-0">
        <p className="text-[11px] text-foreground leading-relaxed">{insight.text}</p>
        {insight.action && (
          <button onClick={() => onAction(insight.action)}
            className={`mt-1.5 inline-flex items-center gap-1 text-[10px] font-medium ${st.color} hover:opacity-80 transition-opacity`}
            data-testid="insight-action-btn">
            {insight.action.label} <ChevronRight size={10} />
          </button>
        )}
      </div>
    </div>
  );
}

function SuggestionCard({ suggestion, onAction }) {
  const meta = SKILL_META[suggestion.skill] || { label: 'General', icon: Sparkles, color: 'violet' };
  const Icon = meta.icon;
  return (
    <div className="flex gap-2.5 p-2.5 rounded-lg border border-border bg-muted hover:bg-muted transition-colors"
      data-testid={`suggestion-${suggestion.skill}`}>
      <div className={`h-7 w-7 rounded-lg bg-${meta.color}-500/10 flex items-center justify-center shrink-0`}>
        <Icon size={13} className={`text-${meta.color}-400`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] text-foreground leading-relaxed">{suggestion.text}</p>
        {suggestion.action && (
          <button onClick={() => onAction(suggestion.action)}
            className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-medium text-emerald-400 hover:opacity-80 transition-opacity"
            data-testid="suggestion-action-btn">
            {suggestion.action.label} <ChevronRight size={10} />
          </button>
        )}
      </div>
      {suggestion.score != null && (
        <span className={`text-[10px] font-bold shrink-0 ${suggestion.score >= 60 ? 'text-amber-400' : 'text-red-400'}`}>
          {suggestion.score}/100
        </span>
      )}
    </div>
  );
}

export default function AiCoachPanel() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');
  const [expanded, setExpanded] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchCoach = useCallback(async (p) => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/coach/insights?period=${p}`, { headers });
      setData(d);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchCoach(period); }, [period, fetchCoach]);

  const handleAction = (action) => {
    if (!action) return;
    if (action.type === 'simulation') {
      navigate('/call-simulation');
    } else if (action.type === 'link') {
      navigate(action.to);
    }
  };

  if (loading && !data) {
    return (
      <Card className="border-border bg-card" data-testid="ai-coach-panel">
        <CardContent className="p-6 flex items-center justify-center">
          <div className="h-6 w-6 rounded-full border-2 border-violet-500 border-t-transparent animate-spin" />
        </CardContent>
      </Card>
    );
  }

  if (!data?.has_data) {
    return (
      <Card className="border-border bg-card" data-testid="ai-coach-panel">
        <CardContent className="p-8 flex flex-col items-center text-center">
          <div className="h-14 w-14 rounded-2xl bg-violet-500/8 border border-violet-500/15 flex items-center justify-center mb-3">
            <BrainCircuit size={24} className="text-violet-500/40" />
          </div>
          <h3 className="text-base font-semibold text-foreground mb-1">AI Coach needs more data</h3>
          <p className="text-xs text-muted-foreground max-w-xs mb-4">
            Run simulations or make calls to unlock personalized coaching insights.
          </p>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => navigate('/call-simulation')}
              className="bg-violet-500/15 text-violet-400 hover:bg-violet-500/25 border border-violet-500/20 h-7 text-xs px-3"
              data-testid="coach-empty-sim-btn">
              <MessageSquare size={12} className="mr-1" /> Run Simulation
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const { skills, insights, suggestions, trends, summary, stats } = data;
  const overall = skills?.overall || 0;
  const gradeColor = overall >= 75 ? 'text-emerald-400' : overall >= 55 ? 'text-amber-400' : 'text-red-400';
  const gradeBg = overall >= 75 ? 'bg-emerald-500/10 border-emerald-500/20' : overall >= 55 ? 'bg-amber-500/10 border-amber-500/20' : 'bg-red-500/10 border-red-500/20';

  return (
    <Card className="border-violet-500/15 bg-gradient-to-br from-violet-950/15 to-card" data-testid="ai-coach-panel">
      <CardHeader className="pb-2 pt-3.5 px-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-foreground text-sm flex items-center gap-1.5">
            <BrainCircuit size={15} className="text-violet-400" />
            AI Coach
          </CardTitle>
          <div className="flex items-center gap-1.5">
            <div className="flex items-center gap-0.5 bg-muted rounded-md p-0.5" data-testid="coach-period-toggle">
              {[{ k: '7d', l: '7D' }, { k: '30d', l: '30D' }, { k: '90d', l: '90D' }].map(p => (
                <button key={p.k} onClick={() => setPeriod(p.k)}
                  className={`px-2 py-0.5 rounded text-[10px] font-medium transition-all ${
                    period === p.k ? 'bg-violet-500/15 text-violet-400' : 'text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid={`coach-period-${p.k}`}>
                  {p.l}
                </button>
              ))}
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="px-4 pb-4 space-y-4">
        {/* Overall Score + Summary Row */}
        <div className="flex gap-4">
          {/* Score Ring */}
          <div className={`h-16 w-16 shrink-0 rounded-2xl border-2 ${gradeBg} flex flex-col items-center justify-center`}
            data-testid="coach-overall-score">
            <span className={`text-2xl font-bold ${gradeColor}`}>{overall}</span>
            <span className={`text-[8px] ${gradeColor} opacity-70`}>OVERALL</span>
          </div>

          {/* Summary */}
          <div className="flex-1 min-w-0">
            <p className="text-[11px] text-foreground leading-relaxed" data-testid="coach-summary">{summary}</p>
            {trends?.conversion_score_change != null && trends.conversion_score_change !== 0 && (
              <Badge className={`mt-1.5 text-[9px] gap-0.5 ${
                trends.conversion_score_change > 0
                  ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                  : 'bg-red-500/10 text-red-400 border-red-500/20'
              }`} data-testid="coach-trend-badge">
                {trends.conversion_score_change > 0 ? <TrendingUp size={9} /> : <TrendingDown size={9} />}
                {trends.conversion_score_change > 0 ? '+' : ''}{trends.conversion_score_change}% conversion vs prior period
              </Badge>
            )}
          </div>
        </div>

        {/* Skills + Insights side-by-side */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Skill Breakdown */}
          <div className="space-y-2" data-testid="skills-breakdown">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">Skill Breakdown</p>
            {Object.entries(skills)
              .filter(([k]) => k !== 'overall')
              .sort(([, a], [, b]) => a - b)
              .map(([name, score]) => (
                <SkillBar key={name} name={name} score={score} />
              ))}
          </div>

          {/* Top Insights */}
          <div className="space-y-2" data-testid="top-insights">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">Top Insights</p>
            {insights?.slice(0, 3).map((ins, i) => (
              <InsightRow key={i} insight={ins} onAction={handleAction} />
            ))}
          </div>
        </div>

        {/* Coaching Suggestions (expandable) */}
        {suggestions?.length > 0 && (
          <div className="space-y-2">
            <button onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1 text-[10px] text-muted-foreground uppercase tracking-wider font-medium hover:text-foreground transition-colors"
              data-testid="toggle-suggestions-btn">
              <Zap size={10} className="text-amber-400" />
              Coaching Suggestions ({suggestions.length})
              <ChevronRight size={10} className={`transition-transform ${expanded ? 'rotate-90' : ''}`} />
            </button>
            {expanded && (
              <div className="space-y-2 animate-in slide-in-from-top-2 duration-200">
                {suggestions.map((s, i) => (
                  <SuggestionCard key={i} suggestion={s} onAction={handleAction} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Quick Stats Footer */}
        <div className="flex items-center justify-between pt-2 border-t border-border/30">
          <div className="flex items-center gap-3">
            {[
              { label: 'Calls', value: stats?.calls },
              { label: 'Sims', value: stats?.simulations },
              { label: 'Bookings', value: stats?.bookings },
            ].map(s => (
              <div key={s.label} className="flex items-center gap-1">
                <span className="text-[10px] text-muted-foreground">{s.label}:</span>
                <span className="text-[10px] font-medium text-foreground">{s.value}</span>
              </div>
            ))}
          </div>
          <Button size="sm" variant="ghost" onClick={() => navigate('/analytics')}
            className="text-violet-400 hover:text-violet-300 h-6 text-[10px] px-2 gap-1"
            data-testid="coach-view-analytics-btn">
            View Analytics <ArrowRight size={10} />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
